// Kassenbon: Foto → KI-Auswertung → editierbarer Vorschlag → buchen.
// Neu: Preise und Markt werden gespeichert (Ausgaben-Statistik), und je
// Position lässt sich das Haltbarkeitsdatum mitgeben.
import { formatEuro } from '@speis/shared';
import type { ProductView, ReceiptSuggestion } from '@speis/shared';
import { api } from '../core/api.js';
import { guard, guarded } from '../core/guard.js';
import { refreshExpiring } from '../core/sse.js';
import { applyProducts, state } from '../core/store.js';
import { clear, el, replace } from '../ui/dom.js';
import { icon } from '../ui/icons.js';
import { openSheet } from '../ui/sheet.js';
import { toast } from '../ui/toast.js';
import { pickImage } from './image.js';

interface ParseResponse {
  store: string | null;
  date: string | null;
  total: number | null;
  suggestions: (ReceiptSuggestion & { suggestedBestBefore: string | null })[];
}

interface Row {
  rawLabel: string;
  cleanName: string;
  brand: string | null;
  qty: number;
  unitPrice: number | null;
  totalPrice: number | null;
  productId: number | null;
  productName: string | null;
  wasAutoMatchedTo: number | null;
  via: string | null;
  bestBefore: string | null;
  createProduct: { name: string; brand: string | null } | null;
  include: boolean;
}

export async function openReceiptFlow(): Promise<void> {
  const image = await pickImage();
  if (!image) return;

  const loading = openSheet(
    el('div', { class: 'loading-block' }, [
      el('span', { class: 'spinner' }),
      el('p', { class: 'muted' }, 'Bon wird gelesen … das dauert ein paar Sekunden.'),
    ]),
    { title: 'Bon scannen', dismissable: false },
  );

  const parsed = await guard(
    () => api<ParseResponse>('/api/receipt/parse', { method: 'POST', body: { image } }),
    { failure: 'Bon konnte nicht gelesen werden' },
  );
  loading.close();

  if (!parsed) return;
  if (!parsed.suggestions.length) {
    toast('Keine Positionen erkannt – vielleicht schärfer oder heller fotografieren.');
    return;
  }
  showSuggestions(parsed);
}

function showSuggestions(parsed: ParseResponse): void {
  const rows: Row[] = parsed.suggestions.map((s) => ({
    rawLabel: s.rawLabel,
    cleanName: s.cleanName,
    brand: s.brand,
    qty: s.qty,
    unitPrice: s.unitPrice,
    totalPrice: s.totalPrice,
    productId: s.match?.productId ?? null,
    productName: s.match?.name ?? null,
    wasAutoMatchedTo: s.match?.productId ?? null,
    via: s.match?.via ?? null,
    bestBefore: s.suggestedBestBefore,
    createProduct: null,
    include: true,
  }));

  const store = el('input', {
    class: 'input',
    type: 'text',
    value: parsed.store ?? '',
    placeholder: 'Markt (z.B. REWE)',
  });
  const list = el('div', { class: 'receipt-list' });
  const summary = el('p', { class: 'tiny tabular' });

  const body = el('div', {}, [
    el('div', { class: 'grid-2' }, [
      el('div', { class: 'field' }, [el('label', {}, 'Markt'), store]),
      el('div', { class: 'field' }, [
        el('label', {}, 'Datum'),
        el('input', { class: 'input', type: 'date', value: parsed.date ?? '', id: 'receipt-date' }),
      ]),
    ]),
    el(
      'p',
      { class: 'field-hint' },
      'Prüfen, zuordnen, übernehmen. Unbekannte Positionen einmal zuordnen – die App merkt sich das Bon-Kürzel dauerhaft.',
    ),
    list,
    summary,
  ]);

  const sheet = openSheet(body, { title: 'Bon-Positionen' });

  function draw(): void {
    clear(list);
    rows.forEach((row, index) => list.append(rowEl(row, index)));
    const total = rows
      .filter((r) => r.include)
      .reduce((sum, r) => sum + (r.totalPrice ?? (r.unitPrice ?? 0) * r.qty), 0);
    summary.textContent =
      total > 0 ? `Summe der ausgewählten Positionen: ${formatEuro(total)}` : '';
  }

  function rowEl(row: Row, index: number): HTMLElement {
    const include = el(
      'button',
      {
        class: 'checkbox',
        type: 'button',
        role: 'checkbox',
        'aria-checked': String(row.include),
        'aria-label': `${row.rawLabel} übernehmen`,
        onclick: () => {
          row.include = !row.include;
          draw();
        },
      },
      icon('check', { size: 14 }),
    );

    const qty = el('input', {
      class: 'input qty-input',
      type: 'number',
      min: 1,
      value: row.qty,
      'aria-label': 'Anzahl',
    });
    qty.addEventListener('change', () => {
      row.qty = Math.max(1, Number(qty.value) || 1);
      draw();
    });

    const assigned = row.productId != null || row.createProduct != null;
    const assignText = row.productId
      ? `→ ${row.productName}`
      : row.createProduct
        ? `＋ neu: ${row.createProduct.name}`
        : 'zuordnen …';

    const assign = el(
      'button',
      {
        class: `assign-btn${assigned ? ' is-assigned' : ''}`,
        type: 'button',
        onclick: () => openPicker(row, draw),
      },
      [
        assignText,
        row.via && row.via === 'fuzzy' ? el('span', { class: 'tiny' }, ' (unsicher)') : null,
      ],
    );

    const date = el('input', {
      class: 'input date-input',
      type: 'date',
      value: row.bestBefore ?? '',
      'aria-label': `Haltbarkeitsdatum für ${row.cleanName}`,
    });
    date.addEventListener('change', () => {
      row.bestBefore = date.value || null;
    });

    return el(
      'div',
      { class: `receipt-row${row.include ? '' : ' is-excluded'}`, 'data-index': index },
      [
        include,
        qty,
        el('div', { class: 'receipt-main' }, [
          el('div', { class: 'item-name' }, row.rawLabel),
          assign,
          el('div', { class: 'receipt-meta' }, [
            row.unitPrice != null
              ? el('span', { class: 'tiny tabular' }, formatEuro(row.unitPrice))
              : null,
            date,
          ]),
        ]),
      ],
    );
  }

  draw();

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            const positions = rows
              .filter((r) => r.include && (r.productId != null || r.createProduct))
              .map((r) => ({
                rawLabel: r.rawLabel,
                qty: r.qty,
                productId: r.productId,
                wasAutoMatchedTo: r.wasAutoMatchedTo,
                unitPrice: r.unitPrice,
                totalPrice: r.totalPrice,
                bestBefore: r.bestBefore,
                ...(r.createProduct ? { createProduct: r.createProduct } : {}),
              }));

            if (!positions.length) {
              toast('Nichts zugeordnet – bitte mindestens eine Position zuordnen.');
              return;
            }

            const dateField = body.querySelector<HTMLInputElement>('#receipt-date');
            const result = await api<{ booked: unknown[]; products: ProductView[] }>(
              '/api/receipt/commit',
              {
                method: 'POST',
                body: {
                  store: store.value.trim() || null,
                  date: dateField?.value || null,
                  total: parsed.total,
                  positions,
                },
              },
            );
            applyProducts(result.products);
            void refreshExpiring();
            sheet.close();
            toast(`${result.booked.length} Positionen gebucht`);
          },
          { failure: 'Buchen fehlgeschlagen' },
        ),
      },
      'Übernehmen & buchen',
    ),
  );
}

function openPicker(row: Row, onDone: () => void): void {
  const search = el('input', {
    class: 'input',
    type: 'search',
    placeholder: 'Produkt suchen …',
    value: row.cleanName,
  });
  const results = el('div', { class: 'results' });
  const body = el('div', {}, [
    el('p', { class: 'tiny' }, `Bon-Text: ${row.rawLabel}`),
    el('div', { class: 'search' }, [icon('search', { size: 18 }), search]),
    results,
  ]);
  const sheet = openSheet(body, { title: 'Position zuordnen' });

  const draw = () => {
    const q = search.value.trim().toLowerCase();
    const hits = state.products
      .filter((p) => !q || `${p.name} ${p.brand ?? ''}`.toLowerCase().includes(q))
      .slice(0, 25);

    clear(results);
    for (const p of hits) {
      results.append(
        el(
          'button',
          {
            class: 'result',
            type: 'button',
            onclick: () => {
              row.productId = p.id;
              row.productName = p.name;
              row.createProduct = null;
              row.via = 'manual';
              sheet.close();
              onDone();
            },
          },
          p.brand ? `${p.name} · ${p.brand}` : p.name,
        ),
      );
    }

    const displayName = row.cleanName || row.rawLabel.slice(0, 30);
    results.append(
      el(
        'button',
        {
          class: 'result result-new',
          type: 'button',
          onclick: () => {
            row.productId = null;
            row.productName = null;
            row.createProduct = { name: displayName, brand: row.brand };
            row.via = null;
            sheet.close();
            onDone();
          },
        },
        [icon('plus', { size: 18 }), `Neu anlegen: „${displayName}"`],
      ),
      el(
        'button',
        {
          class: 'result',
          type: 'button',
          onclick: () => {
            row.include = false;
            sheet.close();
            onDone();
          },
        },
        'Position überspringen',
      ),
    );
  };

  search.addEventListener('input', draw);
  draw();
}

export { replace };
